<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb name="Listings"/>
    <b-row>
      <b-col cols="12">
        <admin-panel title="Listings" with-footer>
          <admin-listings-table :listings="listings"/>
          <template v-slot:header-right>
            <div class="mt-n2 d-flex flex-wrap">
              <admin-companies-autocomplete class="mr-2 mt-2" v-model="filters.company"/>
              <b-form-select
                  class="w-auto mr-2 mt-2"
                  v-model="filters.is_promoted"
                  :options="promoted_filter_options"
                  @change="() => loadListings()"
              />
              <b-form-select
                  class="w-auto mr-2 mt-2"
                  v-model="filters.category_type"
                  :options="categoryTypes"
                  @change="() => loadListings()"
              />
              <b-form-select
                  class="w-auto mr-2 mt-2"
                  v-model="filters.status"
                  :options="statuses"
                  @change="() => loadListings()"
              />
              <b-form-select
                  class="w-auto mr-2 mt-2"
                  v-model="filters.category"
                  :options="categories"
                  @change="() => loadListings()"
              />
              <b-form-select
                  class="w-auto mr-2 mt-2"
                  v-model="filters.production_type"
                  :options="productionTypes"
                  @change="() => loadListings()"
              />
              <b-form-select
                  class="w-auto mt-2"
                  v-model="filters.strain_type"
                  :options="strainTypes"
                  @change="() => loadListings()"
              />
            </div>
          </template>
          <template v-slot:footer>
            <b-pagination
                v-model="page"
                :total-rows="total_in_pages"
                :per-page="per_page"
                @change="loadListings"
                align="center"
                class="admin-pagination mb-0"
            />
          </template>
        </admin-panel>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import {API_ROUTES} from "../api";
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import AdminListingsTable from "../components/AdminListingsTable";
import AdminCompaniesAutocomplete from "../components/AdminCompaniesAutocomplete";

export default {
  name: "AdminListings",
  components: {AdminCompaniesAutocomplete, AdminListingsTable, AdminPanel, AdminBreadcrumb, AdminPreloader},
  data() {
    return {
      listings: [],
      loading: true,
      page: 1,
      per_page: 10,
      total_in_pages: 0,
      filters: {
        company: null,
        status: "",
        category_type: "",
        is_promoted: "",
        category: "",
        strain_type: "",
        production_type: ""
      },
      promoted_filter_options: [
        {
          value: "",
          text: "All Listings"
        },
        {
          value: "1",
          text: "Only Featured"
        },
        {
          value: "0",
          text: "Only Non-Featured"
        },
      ]
    }
  },
  computed: {
    search() {
      return this.$store.state.current.search;
    },
    statuses() {
      let statuses = this.$store.state.data.listing_statuses
          .filter(status => ['pending', 'active', 'lost', 'sold', 'cancelled'].indexOf(status.code) >= 0)
          .map(status => ({
            value: status.id,
            text: status.name
          }));
      statuses.unshift({
        value: '',
        text: 'All Statuses'
      });
      return statuses;
    },
    categoryTypes() {
      let categoryTypes = this.$store.state.data.category_types
          .map(categoryType => ({
            value: categoryType.id,
            text: categoryType.name
          }));
      categoryTypes.unshift({
        value: '',
        text: 'All Category Types'
      });
      return categoryTypes;
    },
    categories() {
      let categories = this.$store.state.data.categories
          .filter(category => category.name !== 'Other')
          .map(category => ({
            value: category.id,
            text: category.name,
            category_type_id: category.category_type_id
          }));
      if (this.filters.category_type) {
        categories = categories.filter(category => category.category_type_id === this.filters.category_type);
      }
      categories.unshift({
        value: '',
        text: 'All Categories'
      });
      return categories;
    },
    productionTypes() {
      let productionTypes = this.$store.state.data.production_types.map(productionType => ({
        value: productionType.id,
        text: productionType.name,
        category_types: productionType.category_types
      }));
      if (this.filters.category_type) {
        productionTypes = productionTypes.filter(productionType => productionType.category_types.indexOf(this.filters.category_type) >= 0);
      }
      productionTypes.unshift({
        value: '',
        text: 'All Productions'
      });
      return productionTypes;
    },
    strainTypes() {
      let strainTypes = this.$store.state.data.strain_types.map(strainType => ({
        value: strainType.id,
        text: strainType.name
      }));
      strainTypes.unshift({
        value: '',
        text: 'All Strain Types'
      });
      return strainTypes;
    }
  },
  watch: {
    search() {
      this.loadListings();
    },
    "filters.company": function() {
      this.loadListings();
    },
    "filters.category_type": function (categoryTypeId) {
      if (categoryTypeId) {
        if (this.filters.category) {
          let category = this.$store.state.data.categories.find(
              category => category.id === this.filters.category && category.category_type_id === categoryTypeId
          );
          if (!category) {
            this.filters.category = '';
          }
        }
        if (this.filters.production_type) {
          let productionType = this.$store.state.data.production_types.find(
              productionType => productionType.id === this.filters.production_types && productionType.category_types.indexOf(categoryTypeId) >= 0
          );
          if (!productionType) {
            this.filters.production_type = '';
          }
        }
      }
    }
  },
  created() {
    this.loadListings();
  },
  methods: {
    loadListings(page = 1) {
      this.loading = true;
      let params = {
        page: page,
        per_page: this.per_page
      };
      if (this.search) {
        params['search'] = this.search;
      }
      if (this.filters.company) {
        params['company'] = this.filters.company.id;
      }
      if (this.filters.status) {
        params['status'] = this.filters.status;
      }
      if (this.filters.category_type) {
        params['category_type'] = this.filters.category_type;
      }
      if (this.filters.category) {
        params['category'] = this.filters.category;
      }
      if (this.filters.production_type) {
        params['production_type'] = this.filters.production_type;
      }
      if (this.filters.strain_type) {
        params['strain_type'] = this.filters.strain_type;
      }
      if (this.filters.is_promoted !== '') {
        params['is_promoted'] = this.filters.is_promoted;
      }
      this.$api.get(API_ROUTES.ADMIN_LISTINGS, {
        params: params
      }).then(({data}) => {
        this.listings = data.listings;
        this.total_in_pages = data.meta.total;
      }).catch(({response}) => {

      }).finally(() => {
        if (page !== this.page) {
          this.page = page;
        }
        this.loading = false;
      });
    }
  }
}
</script>
